/**
 * Box Types Configuration
 * Defines all available box types with their metadata
 */

const CDN_BASE = 'https://cdn.chance.cc/boxes'

export const BOX_TYPES = {
	polymarket: {
		id: 'polymarket',
		name: 'Polymarket Box',
		description: 'Earned by linking your Polymarket account',
		image: `${CDN_BASE}/Polymarket%20with%20light.png`,
		gif: null,
		color: '#036fff',
		rarity: 'common',
	},
	bronze: {
		id: 'bronze',
		name: 'Bronze Box',
		description: 'A starter reward box',
		image: `${CDN_BASE}/Chance%20Crate%20Bronze.png`,
		gif: `${CDN_BASE}/BronzeChestGif.gif`,
		color: '#CD7F32',
		rarity: 'common',
	},
	silver: {
		id: 'silver',
		name: 'Silver Box',
		description: 'A valuable silver reward box',
		image: `${CDN_BASE}/Chance%20Crate%20Silver.png`,
		gif: `${CDN_BASE}/SilverChestGif.gif`,
		color: '#C0C0C0',
		rarity: 'uncommon',
	},
	gold: {
		id: 'gold',
		name: 'Gold Box',
		description: 'A premium gold reward box',
		image: `${CDN_BASE}/Chance%20Crate%20Gold.png`,
		gif: `${CDN_BASE}/GoldChestGif.gif`,
		color: '#FFD700',
		rarity: 'rare',
	},
	diamond: {
		id: 'diamond',
		name: 'Diamond Box',
		description: 'An extremely rare diamond reward box',
		image: `${CDN_BASE}/Chance%20Crate%20Diamond.png`,
		gif: `${CDN_BASE}/DiamondChestGif.gif`,
		color: '#B9F2FF',
		rarity: 'legendary',
	},
	opinionlabs: {
		id: 'opinionlabs',
		name: 'Opinion Box',
		description: 'Earned through OpinionLabs partnership',
		image: `${CDN_BASE}/Opinion%20with%20light.png`,
		gif: `${CDN_BASE}/Opinion%20gif.gif`,
		color: '#9B59B6',
		rarity: 'uncommon',
	},
	cookiedao: {
		id: 'cookiedao',
		name: 'CookieDAO Box',
		description: 'Earned through CookieDAO partnership',
		image: `${CDN_BASE}/CookieDao%20with%20light.png`,
		gif: `${CDN_BASE}/CookieDao%20gif.gif`,
		color: '#F5A623',
		rarity: 'uncommon',
	},
	moonpay: {
		id: 'moonpay',
		name: 'MoonPay Box',
		description: 'Earned through MoonPay partnership',
		image: `${CDN_BASE}/MoonPay%20with%20light.png`,
		gif: `${CDN_BASE}/MoonPay%20gif.gif`,
		color: '#7B61FF',
		rarity: 'uncommon',
	},
	creator: {
		id: 'creator',
		name: 'Creator Box',
		description: 'Earned by linking your Twitter creator account',
		image: `${CDN_BASE}/c2.png`,
		gif: null,
		color: '#7c5ea0',
		rarity: 'uncommon',
	},
} as const

export type BoxType = keyof typeof BOX_TYPES

export const BOX_TYPE_LIST = Object.keys(BOX_TYPES) as BoxType[]

/**
 * Polymarket box reward configuration
 * - $0 - $100: 0 boxes
 * - $100 - $1,000: 1 box
 * - Every additional $3,000 above $1,000: 1 box
 * - Cap at 100 boxes (reached at ~$298k volume)
 */
export const POLYMARKET_BOX_CONFIG = {
	minVolumeForFirstBox: 100, // Minimum $100 to get first box
	firstTierMax: 1000, // First tier: $100-$1,000 = 1 box
	volumePerAdditionalBox: 3000, // Every $3k above $1k = 1 additional box
	maxBoxes: 100, // Cap at 100 boxes
} as const

/**
 * Calculate how many Polymarket boxes a user should receive based on their trading volume
 *
 * Rules:
 * - $0 - $100: 0 boxes
 * - $100 - $1,000: 1 box
 * - Every additional $3,000 above $1,000: 1 box
 * - Cap at 100 boxes
 *
 * @param volume - Total trading volume in USD
 * @returns Number of Polymarket boxes to grant
 */
export function calculatePolymarketBoxesForVolume(volume: number): number {
	const { minVolumeForFirstBox, firstTierMax, volumePerAdditionalBox, maxBoxes } =
		POLYMARKET_BOX_CONFIG

	// $0 - $100: 0 boxes
	if (volume < minVolumeForFirstBox) {
		return 0
	}

	// $100 - $1,000: 1 box
	if (volume < firstTierMax) {
		return 1
	}

	// $1,000+: 1 box + 1 box per $3,000 above $1,000
	const additionalBoxes = Math.floor((volume - firstTierMax) / volumePerAdditionalBox)
	const totalBoxes = 1 + additionalBoxes

	// Cap at 100 boxes
	return Math.min(totalBoxes, maxBoxes)
}

/**
 * Get box metadata by type
 */
export function getBoxMetadata(boxType: BoxType) {
	return BOX_TYPES[boxType]
}

/**
 * Get rarity color for UI display
 */
export function getRarityColor(rarity: string): string {
	switch (rarity) {
		case 'common':
			return 'text-white/60'
		case 'uncommon':
			return 'text-green-400'
		case 'rare':
			return 'text-blue-400'
		case 'legendary':
			return 'text-purple-400'
		default:
			return 'text-white/60'
	}
}

/**
 * Get rarity background color for UI display
 */
export function getRarityBgColor(rarity: string): string {
	switch (rarity) {
		case 'common':
			return 'bg-white/10'
		case 'uncommon':
			return 'bg-green-500/20'
		case 'rare':
			return 'bg-blue-500/20'
		case 'legendary':
			return 'bg-purple-500/20'
		default:
			return 'bg-white/10'
	}
}
